/**
 * LegacyPaymentSummary Component
 *
 * Sub-component for displaying payment summary for legacy events using costPerAttendee.
 * This component is isolated to prevent regressions when updating payment display logic.
 *
 * Features:
 * - Displays ticket count with cost per ticket
 * - Shows paid tickets pill when applicable
 * - Maintains consistency with tiered ticket display patterns
 */

import { css } from 'aphrodite';
import * as React from 'react';
import { Pill } from '../../../../../../../../web/components-v2/Pill/Pill';
import { bs } from '../../../../../../../../web/styles/styles';
import { styleSheet } from '../styles';

interface LegacyPaymentSummaryProps {
	/** Number of tickets in the order */
	ticketsOrder: number;
	/** Number of tickets already paid for */
	paidTickets: number;
	/** Cost per attendee in cents (e.g., 5000 = $50.00) */
	costPerAttendee: number;
}

/**
 * Legacy Payment Summary Component
 *
 * Displays payment breakdown for legacy events that use costPerAttendee instead of tiered tickets.
 * Shows ticket count with cost per ticket information.
 */
export const LegacyPaymentSummary: React.FC<LegacyPaymentSummaryProps> = ({
	ticketsOrder,
	paidTickets,
	costPerAttendee,
}) => {
	// Format cost per ticket in USD
	const formattedCostPerTicket = React.useMemo(() => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(costPerAttendee / 100);
	}, [costPerAttendee]);

	return (
		<section className={css(bs.flex, bs.gap2, bs.itemsCenter)}>
			<p>
				Tickets × <span className={css(styleSheet?.ticketCountText)}>{ticketsOrder}</span> @{' '}
				<span className={css(styleSheet?.priceAmount)}>{formattedCostPerTicket}/ticket</span>
			</p>
			{paidTickets ? (
				<div className={css(styleSheet.paidTickets)}>
					<Pill label={`Paid Tickets: ${paidTickets}`} variant='label' color={'green'} />
				</div>
			) : null}
		</section>
	);
};
